import { api } from "../../../../api";
import { PackingPropsResponse } from "../unloading/types";
import {
  BillLadingCommonProps,
  BillLadingCreate,
  CommoditiesProps,
  CreateCommoditiesFields,
  CreateVoyage,
  MaritimeEquipment,
  VoyageInfo,
} from "./types";

export async function fetchBillLadingApi(
  id: number,
): Promise<{ items: BillLadingCommonProps[] }> {
  const { data } = await api.get<{ items: BillLadingCommonProps[] }>(
    `/api/shipping-cases/${id}/bill-of-lading`,
  );
  return data;
}

export async function createBillLadingApi(
  id: number,
  params: Partial<BillLadingCreate>,
): Promise<BillLadingCommonProps> {
  const { data } = await api.post<BillLadingCommonProps>(
    `/api/shipping-cases/${id}/bill-of-lading`,
    params,
  );
  return data;
}

export async function fetchBillLadingItemApi(
  id: number,
): Promise<BillLadingCommonProps> {
  const { data } = await api.get<BillLadingCommonProps>(
    `/api/bill-of-lading/${id}`,
  );
  return data;
}

export async function fetchPackingListApi(params: {
  entity_id: number;
  entity: string;
  page: number;
  size: number;
}): Promise<PackingPropsResponse> {
  const { data } = await api.get<PackingPropsResponse>(
    `api/packing-lists/unsafe-to-delete`,
    { params },
  );
  return data;
}

export async function updateBillLadingApi(
  id: number,
  params: Partial<BillLadingCreate>,
): Promise<void> {
  await api.patch<void>(`/api/bill-of-lading/${id}`, params);
}

export async function getVoyageApi(
  id: number,
): Promise<{ items: VoyageInfo[] }> {
  const { data } = await api.get<{ items: VoyageInfo[] }>(
    `/api/shipping-cases/${id}/voyages`,
  );
  return data;
}

export async function createVoyageApi(
  id: number,
  params: Partial<CreateVoyage>,
): Promise<void> {
  await api.post<void>(`/api/shipping-cases/${id}/voyages`, params);
}

export async function updateVoyageApi(
  id: number,
  voyageId: number,
  params: Partial<CreateVoyage>,
): Promise<void> {
  await api.patch<void>(
    `/api/shipping-cases/${id}/voyages/${voyageId}`,
    params,
  );
}

export async function getCommoditiesApi(
  id: number,
): Promise<{ items: CommoditiesProps[] }> {
  const { data } = await api.get<{ items: CommoditiesProps[] }>(
    `/api/bill-of-lading/${id}/commodities`,
  );
  return data;
}

export async function createCommodityApi(
  params: Partial<CreateCommoditiesFields>,
  id: number,
): Promise<CommoditiesProps> {
  const { data } = await api.post<CommoditiesProps>(
    `/api/bill-of-lading/${id}/commodities`,
    params,
  );
  return data;
}

export async function updateCommodityApi(
  params: Partial<CreateCommoditiesFields>,
  editId: number,
): Promise<CommoditiesProps> {
  const { data } = await api.patch<CommoditiesProps>(
    `/api/bl-commodities/${editId}`,
    params,
  );
  return data;
}

export async function removeCommodityApi(id: number): Promise<void> {
  await api.delete<void>(`/api/bl-commodities/${id}`);
}

export async function removeBillApi(id: number): Promise<void> {
  await api.delete<void>(`/api/bill-of-lading/${id}`);
}

export async function getContainersApi(
  id: number,
): Promise<{ items: MaritimeEquipment[] }> {
  const { data } = await api.get<{ items: MaritimeEquipment[] }>(
    `/api/bl-commodities/${id}/containers`,
  );
  return data;
}

export async function getContainerByIdApi(
  id: number,
): Promise<MaritimeEquipment> {
  const { data } = await api.get<MaritimeEquipment>(`/api/bl-containers/${id}`);
  return data;
}

export async function createContainerApi(
  params: Partial<MaritimeEquipment>,
  id: number,
): Promise<MaritimeEquipment> {
  const { data } = await api.post<MaritimeEquipment>(
    `/api/bl-commodities/${id}/containers`,
    params,
  );
  return data;
}

export async function updateContainerApi(
  params: Partial<MaritimeEquipment>,
  id: number,
): Promise<MaritimeEquipment> {
  const { data } = await api.patch<MaritimeEquipment>(
    `/api/bl-containers/${id}`,
    params,
  );
  return data;
}

export async function removeContainerApi(id: number): Promise<void> {
  await api.delete<void>(`/api/bl-containers/${id}`);
}

export async function exportCargoDetailsApi(id: number): Promise<{
  file: Blob;
  filename: string;
}> {
  const response = await api.get(
    `/api/shipping-cases/${id}/bl-containers/export`,
    {
      responseType: "blob",
    },
  );
  const disposition = response.headers["content-disposition"];
  let filename = "downloaded_file";
  if (disposition) {
    const match = disposition.match(/filename="?([^"]+)"?/);
    if (match && match[1]) {
      filename = decodeURIComponent(match[1]);
    }
  }

  return { file: response.data, filename };
}

export async function loadFileApi(data: File, id: number): Promise<void> {
  const form = new FormData();
  form.append("file", data);

  await api.post(`/api/shipping-cases/${id}/bl-containers/import`, form, {
    headers: {
      "Content-Type": undefined,
    },
  });
}
